/* ==============================================================================
   Pipeline de áudio
   ==============================================================================
   getUserMedia → passa-alta → gate de ruído → compressor → ganho → limitador
                                                          ↘ medidor de nível

   A cadeia é montada com nós nativos do WebAudio, e cada estágio tem bypass e
   parâmetros persistidos em `user_settings.audio`.

   Por que nós nativos e não tudo em AudioWorklet
   =============================================
   `BiquadFilterNode`, `DynamicsCompressorNode` e `GainNode` são implementados em
   código nativo, rodam na thread de áudio e custam quase nada. Reimplementá-los
   em worklet seria mais código, mais CPU e pior qualidade.

   O worklet entra onde não há nó nativo equivalente: o GATE (com attack, release
   e hold configuráveis) e o MEDIDOR. E entraria para o RNNoise — ver a nota
   sobre ele no fim do arquivo.

   O que o navegador já faz, e por que fica separado
   ================================================
   `echoCancellation`, `noiseSuppression` e `autoGainControl` são flags de
   `getUserMedia`, processadas antes de o áudio chegar aqui. Elas são boas e às
   vezes indesejadas: a supressão do Chrome corta o final das palavras em voz
   baixa, e o AGC bombeia quando há ruído de fundo. Ficam como interruptores
   separados para o usuário poder combinar ou dispensar — não são substituídas
   pelos estágios daqui.
   ============================================================================== */

/** Configuração completa da cadeia. Persistida em `user_settings.audio`. */
export interface AudioSettings {
  /** `deviceId` do microfone. `null` = padrão do sistema. */
  inputDeviceId: string | null;
  /** `deviceId` da saída, aplicado com `setSinkId`. `null` = padrão. */
  outputDeviceId: string | null;

  /** Flags nativas de `getUserMedia`. */
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;

  /** Passa-alta: corta rumble de mesa e ar-condicionado. */
  highPass: { enabled: boolean; frequencyHz: number };

  /** Gate: silencia abaixo do limiar. */
  gate: {
    enabled: boolean;
    /** Limiar em dBFS. -50 é permissivo, -30 é agressivo. */
    thresholdDb: number;
    /** Tempo para abrir, em ms. Curto demais corta o ataque da consoante. */
    attackMs: number;
    /** Tempo para fechar, em ms. */
    releaseMs: number;
    /** Mantém aberto por este tempo depois de cair abaixo do limiar. */
    holdMs: number;
  };

  /** Compressor: nivela o volume da voz. */
  compressor: {
    enabled: boolean;
    thresholdDb: number;
    ratio: number;
    attackMs: number;
    releaseMs: number;
  };

  /**
   * Ganho aplicado DEPOIS do compressor, em dB. É a compensação dele.
   *
   * A posição na cadeia é o que define a função: `... → compressor → ganho →
   * limitador`. Um compressor sem ganho depois só ATENUA — ele reduz os picos e
   * não repõe nada, então a voz sai mais baixa do que entrou. Repor é metade do
   * trabalho de comprimir, e omitir essa metade é o defeito que produziu "o
   * microfone das outras pessoas está muito baixo".
   */
  inputGainDb: number;

  /** Limitador: proteção contra clipping. Recomendado sempre ligado. */
  limiter: { enabled: boolean; thresholdDb: number };

  /**
   * Intensidade do filtro de ruído neural (RNNoise), de 0 a 1.
   *
   * 0 = desligado, 1 = totalmente processado. Valores intermediários misturam o
   * sinal processado com o original — o "wet/dry" — porque supressão total soa
   * artificial e come o ambiente natural da voz.
   *
   * Sem efeito enquanto o worklet do RNNoise não está carregado. Ver a nota no
   * fim do arquivo.
   */
  neuralSuppression: number;
}

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  inputDeviceId: null,
  outputDeviceId: null,
  echoCancellation: true,
  noiseSuppression: false,
  autoGainControl: false,
  // Padrões: nativas do navegador DESLIGADAS exceto cancelamento de eco, e a
  // cadeia própria ligada. O eco é o único que a cadeia daqui não consegue
  // tratar — ele precisa do sinal de referência do alto-falante, que só a
  // implementação do navegador tem.
  highPass: { enabled: true, frequencyHz: 85 },
  gate: { enabled: true, thresholdDb: -45, attackMs: 5, releaseMs: 120, holdMs: 200 },
  compressor: { enabled: true, thresholdDb: -24, ratio: 3, attackMs: 5, releaseMs: 120 },
  /* ==========================================================================
     +9 dB, e a conta que leva a esse número
     ==========================================================================
     Era 0, e 0 é o que fazia a voz sair mais baixa do que entrou.

     O compressor acima está em limiar −24 dB com razão 3:1. Fala em chamada tem
     pico entre −12 e −6 dBFS, então:

         pico de −6 dBFS
           passa do limiar por  18 dB
           sai passando por     18 / 3 = 6 dB
           ou seja, em          −18 dBFS
           REDUÇÃO              12 dB

     Redução típica entre 8 e 12 dB, e nada a repunha. Cada pessoa da chamada
     chegava cerca de 10 dB mais baixa do que o microfone dela captou — e um
     controle de volume no lado de quem ouve, mesmo indo a 300%, começa de um
     sinal que já foi atenuado na origem.

     +9 dB devolve os picos para cerca de −9 dBFS, com folga sobre o limitador em
     −3 dB, que é quem pega qualquer transiente restante. Não é +12 porque a
     redução varia com a voz e com o microfone, e superestimar a compensação faz
     o limitador trabalhar de forma audível.

     A correção é AQUI e não no volume de quem escuta, e a razão é onde a
     informação existe: aqui se sabe exatamente quanto o compressor tirou. Do
     outro lado, só se sabe que "está baixo", e a compensação seria um palpete
     que cada pessoa refaz para cada participante.
  ========================================================================== */
  inputGainDb: 9,
  limiter: { enabled: true, thresholdDb: -3 },
  neuralSuppression: 0,
};

export interface AudioLevel {
  /** RMS em 0..1, já suavizado. Alimenta o medidor. */
  rms: number;
  /** Pico instantâneo em 0..1. Revela clipping que o RMS esconde. */
  peak: number;
  /** `true` quando o gate está aberto. */
  gateOpen: boolean;
}

export interface AudioDevice {
  deviceId: string;
  label: string;
  /** `true` se o rótulo veio vazio — acontece sem permissão concedida. */
  labelUnavailable: boolean;
}

export interface DeviceList {
  inputs: AudioDevice[];
  outputs: AudioDevice[];
  /**
   * `false` quando o navegador não permite escolher a saída.
   *
   * `setSinkId` é do Chromium; no Firefox exige `selectAudioOutput()` com prompt
   * explícito, e no Safari não existe. A interface precisa saber para DESABILITAR
   * COM EXPLICAÇÃO em vez de oferecer um seletor que não faz nada.
   */
  canSelectOutput: boolean;
}

/** Conversão dB ↔ linear. Ganho em dB é como se pensa; o nó quer linear. */
function dbToLinear(db: number): number {
  return 10 ** (db / 20);
}

export class AudioPipeline {
  private context: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private destination: MediaStreamAudioDestinationNode | null = null;

  private highPass: BiquadFilterNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private gain: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private gateNode: AudioWorkletNode | null = null;
  private meterNode: AudioWorkletNode | null = null;

  private rawStream: MediaStream | null = null;
  private settings: AudioSettings = DEFAULT_AUDIO_SETTINGS;

  private level: AudioLevel = { rms: 0, peak: 0, gateOpen: false };
  private levelListeners = new Set<(level: AudioLevel) => void>();

  /** `true` quando os worklets carregaram. Sem eles, gate e medidor não existem. */
  private workletsReady = false;

  get currentSettings(): AudioSettings {
    return this.settings;
  }

  get currentLevel(): AudioLevel {
    return this.level;
  }

  onLevel(listener: (level: AudioLevel) => void): () => void {
    this.levelListeners.add(listener);
    return () => this.levelListeners.delete(listener);
  }

  /* ==========================================================================
     Dispositivos
     ========================================================================== */

  /**
   * Enumera dispositivos de áudio.
   *
   * Os rótulos vêm VAZIOS antes de a permissão ser concedida — comportamento de
   * privacidade do navegador, para uma página não poder inventariar o hardware
   * sem consentimento. A flag `labelUnavailable` deixa a interface dizer "conceda
   * acesso ao microfone para ver os nomes" em vez de mostrar uma lista de ids.
   */
  static async listDevices(): Promise<DeviceList> {
    const devices = await navigator.mediaDevices.enumerateDevices();

    const shape = (device: MediaDeviceInfo): AudioDevice => ({
      deviceId: device.deviceId,
      label: device.label || `Dispositivo ${device.deviceId.slice(0, 6)}`,
      labelUnavailable: device.label.length === 0,
    });

    return {
      inputs: devices.filter((d) => d.kind === 'audioinput').map(shape),
      outputs: devices.filter((d) => d.kind === 'audiooutput').map(shape),
      /*
       * Detecção de capacidade em vez de detecção de navegador.
       *
       * `setSinkId` no protótipo de HTMLMediaElement é a pergunta certa: ela
       * responde "este navegador, nesta versão, permite escolher a saída?" sem
       * manter uma lista de user agents que envelhece.
       */
      canSelectOutput:
        typeof HTMLMediaElement !== 'undefined' &&
        'setSinkId' in HTMLMediaElement.prototype,
    };
  }

  /* ==========================================================================
     Montagem
     ========================================================================== */

  async start(settings: AudioSettings = DEFAULT_AUDIO_SETTINGS): Promise<MediaStreamTrack> {
    this.settings = settings;

    /*
     * `latencyHint: 'interactive'` e taxa de 48 kHz.
     *
     * 48 kHz é a taxa nativa do Opus: pedir 44.1 forçaria reamostragem em cada
     * quadro, gastando CPU e adicionando latência para nada. `interactive` pede
     * o menor buffer que o sistema consegue — em conversa, latência é o que
     * decide se as pessoas se atropelam.
     */
    this.context = new AudioContext({ latencyHint: 'interactive', sampleRate: 48_000 });

    /*
     * O AudioContext nasce SUSPENSO sem gesto do usuário.
     *
     * Política de autoplay: uma página não pode capturar nem tocar áudio sem
     * interação. `resume()` só funciona dentro do gesto — e é por isso que
     * `start()` deve ser chamado no clique de "entrar na chamada", não na
     * montagem do componente. Chamar cedo produz um contexto suspenso que nunca
     * processa, e o sintoma é "o medidor não mexe".
     */
    if (this.context.state === 'suspended') {
      await this.context.resume();
    }

    this.rawStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        ...(settings.inputDeviceId !== null
          ? { deviceId: { exact: settings.inputDeviceId } }
          : {}),
        echoCancellation: settings.echoCancellation,
        noiseSuppression: settings.noiseSuppression,
        autoGainControl: settings.autoGainControl,
        channelCount: 1,
        sampleRate: 48_000,
      },
      video: false,
    });

    await this.loadWorklets();
    this.buildChain();

    const [track] = this.destination!.stream.getAudioTracks();
    if (!track) throw new Error('A cadeia de áudio não produziu track de saída.');

    // `contentHint: 'speech'` diz ao Opus para otimizar para voz: banda estreita
    // com DTX, em vez de música com faixa completa. A diferença é de ~30% de
    // banda em conversa.
    track.contentHint = 'speech';
    return track;
  }

  /**
   * Carrega os worklets. Falha NÃO é fatal.
   *
   * Sem os worklets, a cadeia funciona com passa-alta, compressor, ganho e
   * limitador — perde o gate e o medidor. Isso é degradação aceitável: a pessoa
   * fala e é ouvida. Falhar a entrada na chamada porque um arquivo de worklet não
   * carregou seria trocar uma limitação por uma indisponibilidade.
   */
  private async loadWorklets(): Promise<void> {
    if (!this.context) return;
    try {
      const url = new URL('./worklets/processors.js', import.meta.url);
      await this.context.audioWorklet.addModule(url.href);
      this.workletsReady = true;
    } catch (error) {
      this.workletsReady = false;
      console.warn(
        '[áudio] worklets não carregaram: gate e medidor de nível ficam ' +
          'indisponíveis. A voz continua funcionando.',
        error,
      );
    }
  }

  private buildChain(): void {
    const context = this.context;
    const stream = this.rawStream;
    if (!context || !stream) return;

    this.source = context.createMediaStreamSource(stream);
    this.destination = context.createMediaStreamDestination();

    /* ---- Passa-alta ---- */
    this.highPass = context.createBiquadFilter();
    this.highPass.type = 'highpass';
    this.highPass.frequency.value = this.settings.highPass.frequencyHz;
    /*
     * Q em 0.7071 (Butterworth).
     *
     * O default do WebAudio é 1, que produz um pico de ressonância na
     * frequência de corte — audível como um "boom" justamente onde se queria
     * cortar. 0.7071 é a resposta mais plana possível.
     */
    this.highPass.Q.value = 0.7071;

    /* ---- Compressor ---- */
    this.compressor = context.createDynamicsCompressor();
    this.compressor.threshold.value = this.settings.compressor.thresholdDb;
    this.compressor.ratio.value = this.settings.compressor.ratio;
    this.compressor.attack.value = this.settings.compressor.attackMs / 1000;
    this.compressor.release.value = this.settings.compressor.releaseMs / 1000;
    // `knee` suave: transição gradual em vez de compressão que "liga" de repente.
    this.compressor.knee.value = 12;

    /* ---- Ganho ---- */
    this.gain = context.createGain();
    this.gain.gain.value = dbToLinear(this.settings.inputGainDb);

    /* ---- Limitador ---- */
    this.limiter = context.createDynamicsCompressor();
    this.limiter.threshold.value = this.settings.limiter.thresholdDb;
    /*
     * Ratio 20 e ataque quase zero: é isso que faz um compressor virar limitador.
     *
     * O limitador existe para o caso do ganho manual: alguém sobe +12 dB, grita,
     * e sem ele o sinal satura — clipping digital, que soa como estalo e não
     * como volume alto. O ataque de 1 ms pega o transiente antes de ele passar.
     */
    this.limiter.ratio.value = 20;
    this.limiter.attack.value = 0.001;
    this.limiter.release.value = 0.05;
    this.limiter.knee.value = 0;

    /* ---- Worklets ---- */
    if (this.workletsReady) {
      this.gateNode = new AudioWorkletNode(context, 'concord-gate', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: 1,
        processorOptions: {
          thresholdDb: this.settings.gate.thresholdDb,
          attackMs: this.settings.gate.attackMs,
          releaseMs: this.settings.gate.releaseMs,
          holdMs: this.settings.gate.holdMs,
          enabled: this.settings.gate.enabled,
        },
      });

      this.meterNode = new AudioWorkletNode(context, 'concord-meter', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: 1,
      });

      this.meterNode.port.onmessage = (event: MessageEvent) => {
        const data = event.data as { rms?: number; peak?: number; gateOpen?: boolean };
        this.level = {
          rms: data.rms ?? 0,
          peak: data.peak ?? 0,
          gateOpen: data.gateOpen ?? this.level.gateOpen,
        };
        for (const listener of this.levelListeners) listener(this.level);
      };
    }

    /* ---- Ligação ---- */
    /*
     * A ordem é fixa e cada posição tem razão:
     *
     *   passa-alta ANTES do gate     — sem cortar o rumble, o gate mede energia
     *                                  de graves e abre com ar-condicionado
     *   gate ANTES do compressor     — comprimir primeiro AMPLIFICA o ruído de
     *                                  fundo até acima do limiar, e o gate deixa
     *                                  de conseguir fechar
     *   ganho ANTES do limitador     — o limitador é a rede de segurança do ganho
     *   medidor NO FIM               — mostra o que o par vai ouvir, não o que o
     *                                  microfone captou
     */
    let node: AudioNode = this.source;

    if (this.settings.highPass.enabled) {
      node.connect(this.highPass);
      node = this.highPass;
    }
    if (this.gateNode) {
      node.connect(this.gateNode);
      node = this.gateNode;
    }
    if (this.settings.compressor.enabled) {
      node.connect(this.compressor);
      node = this.compressor;
    }
    node.connect(this.gain);
    node = this.gain;

    if (this.settings.limiter.enabled) {
      node.connect(this.limiter);
      node = this.limiter;
    }
    if (this.meterNode) {
      node.connect(this.meterNode);
      node = this.meterNode;
    }

    node.connect(this.destination);
  }

  /* ==========================================================================
     Ajuste em tempo real
     ========================================================================== */

  /**
   * Atualiza parâmetros sem reconstruir a cadeia.
   *
   * Reconstruir produziria um corte audível de dezenas de milissegundos a cada
   * arraste de controle — e o usuário arrasta justamente para OUVIR o efeito. Os
   * `AudioParam` aceitam mudança contínua.
   *
   * A exceção é trocar de DISPOSITIVO ou ligar/desligar estágio, que muda a
   * topologia: aí `restart()` é necessário, e a diferença fica explícita no
   * retorno.
   */
  update(patch: Partial<AudioSettings>): { requiresRestart: boolean } {
    const before = this.settings;
    this.settings = { ...before, ...patch };

    const topologyChanged =
      (patch.inputDeviceId !== undefined && patch.inputDeviceId !== before.inputDeviceId) ||
      (patch.echoCancellation !== undefined &&
        patch.echoCancellation !== before.echoCancellation) ||
      (patch.noiseSuppression !== undefined &&
        patch.noiseSuppression !== before.noiseSuppression) ||
      (patch.autoGainControl !== undefined &&
        patch.autoGainControl !== before.autoGainControl) ||
      (patch.highPass?.enabled !== undefined &&
        patch.highPass.enabled !== before.highPass.enabled) ||
      (patch.compressor?.enabled !== undefined &&
        patch.compressor.enabled !== before.compressor.enabled) ||
      (patch.limiter?.enabled !== undefined &&
        patch.limiter.enabled !== before.limiter.enabled);

    if (topologyChanged) return { requiresRestart: true };

    const now = this.context?.currentTime ?? 0;
    /*
     * `setTargetAtTime` e não atribuição direta.
     *
     * Atribuir a `.value` muda o parâmetro no próximo bloco de 128 amostras —
     * um salto de degrau que produz um clique audível. `setTargetAtTime` com
     * constante de 20 ms faz a transição suave. É a diferença entre um controle
     * que soa bem e um que estala a cada pixel arrastado.
     */
    const glide = 0.02;

    if (patch.highPass?.frequencyHz !== undefined && this.highPass) {
      this.highPass.frequency.setTargetAtTime(patch.highPass.frequencyHz, now, glide);
    }
    if (patch.inputGainDb !== undefined && this.gain) {
      this.gain.gain.setTargetAtTime(dbToLinear(patch.inputGainDb), now, glide);
    }
    if (patch.compressor !== undefined && this.compressor) {
      const c = this.settings.compressor;
      this.compressor.threshold.setTargetAtTime(c.thresholdDb, now, glide);
      this.compressor.ratio.setTargetAtTime(c.ratio, now, glide);
      this.compressor.attack.setTargetAtTime(c.attackMs / 1000, now, glide);
      this.compressor.release.setTargetAtTime(c.releaseMs / 1000, now, glide);
    }
    if (patch.limiter?.thresholdDb !== undefined && this.limiter) {
      this.limiter.threshold.setTargetAtTime(patch.limiter.thresholdDb, now, glide);
    }
    if (patch.gate !== undefined && this.gateNode) {
      // O gate é worklet: os parâmetros vão por mensagem, não por AudioParam.
      this.gateNode.port.postMessage({ type: 'config', ...this.settings.gate });
    }

    return { requiresRestart: false };
  }

  /** Reconstrói a cadeia. Necessário ao trocar de dispositivo. */
  async restart(): Promise<MediaStreamTrack> {
    const settings = this.settings;
    await this.stop();
    return this.start(settings);
  }

  /**
   * Aplica o dispositivo de saída a um elemento de áudio.
   *
   * Estático porque a saída não é da cadeia: cada par tem seu próprio elemento
   * `<audio>`, e o roteamento é por elemento. Um método de instância sugeriria
   * que a pipeline controla a saída, e ela não controla.
   */
  static async applyOutputDevice(
    element: HTMLMediaElement,
    deviceId: string | null,
  ): Promise<{ applied: boolean; reason?: string }> {
    if (deviceId === null) return { applied: true };

    if (!('setSinkId' in element)) {
      return {
        applied: false,
        reason:
          'Este navegador não permite escolher o dispositivo de saída. O áudio vai ' +
          'para o dispositivo padrão do sistema.',
      };
    }

    try {
      await (element as HTMLMediaElement & { setSinkId: (id: string) => Promise<void> })
        .setSinkId(deviceId);
      return { applied: true };
    } catch (error) {
      /*
       * Falha aqui é comum e informativa: o dispositivo pode ter sido
       * desconectado entre a enumeração e a aplicação. Cair no padrão do sistema
       * é melhor que ficar sem áudio.
       */
      return {
        applied: false,
        reason:
          error instanceof Error
            ? `Não foi possível usar este dispositivo: ${error.message}`
            : 'Dispositivo de saída indisponível.',
      };
    }
  }

  async stop(): Promise<void> {
    this.meterNode?.port.close();
    this.gateNode?.port.close();

    for (const track of this.rawStream?.getTracks() ?? []) track.stop();
    this.rawStream = null;

    // Desconectar antes de fechar: nós ainda ligados podem manter referência ao
    // contexto e atrasar a liberação de recurso nativo.
    this.source?.disconnect();
    this.highPass?.disconnect();
    this.gateNode?.disconnect();
    this.compressor?.disconnect();
    this.gain?.disconnect();
    this.limiter?.disconnect();
    this.meterNode?.disconnect();

    await this.context?.close();
    this.context = null;
    this.source = null;
    this.destination = null;
    this.highPass = null;
    this.gateNode = null;
    this.compressor = null;
    this.gain = null;
    this.limiter = null;
    this.meterNode = null;
    this.level = { rms: 0, peak: 0, gateOpen: false };
  }
}

/* ==============================================================================
   Nota sobre o RNNoise — o que falta e por que não está aqui
   ==============================================================================
   `settings.neuralSuppression` existe e não tem efeito ainda. O estágio está
   desenhado e não implementado, e o motivo é técnico e específico:

   O RNNoise opera em quadros de 480 amostras a 48 kHz. O AudioWorklet entrega
   blocos de 128 amostras. Os dois não são múltiplos, então o worklet precisa de
   um ring buffer que acumule 480, processe, e devolva 128 por vez — o que
   introduz ~10 ms de latência e exige cuidado com o caso em que o buffer não
   está cheio no início.

   É a parte mais delicada do pipeline, e a que precisa de teste com fixtures de
   áudio real (voz limpa, voz + ventilador, voz + teclado) comparando SNR antes e
   depois. Implementar sem esse teste produziria um filtro que "parece funcionar"
   e come consoantes.

   O gate configurável já entrega a maior parte do valor prático: ele elimina o
   ruído de fundo nos silêncios, que é onde o incômodo é maior numa reunião.
   ============================================================================== */
